import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useState, type SyntheticEvent } from "react";
import { toast } from "sonner";
import { FormField } from "@/components/form-field";
import { Logo } from "@/components/logo";
import { ErrorState, LoadingScreen } from "@/components/states";
import { ThemeToggle } from "@/components/theme-toggle";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { authPost } from "@/lib/auth-client";
import { queries } from "@/lib/queries";

/** Fase 9 — Minha conta: verificação em duas etapas (TOTP), opcional para todos e obrigatória no painel do dono. */
export const Route = createFileRoute("/app/conta")({
  component: ContaPage,
});

interface EnableResult {
  totpURI: string;
  backupCodes: string[];
}

function secretOf(uri: string): string {
  try {
    return new URL(uri).searchParams.get("secret") ?? "";
  } catch {
    return "";
  }
}

function lastWorkspaceId(): string | null {
  try {
    return localStorage.getItem("iagenix-ads-workspace");
  } catch {
    return null;
  }
}

function ContaPage() {
  const me = useQuery(queries.me());
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [setup, setSetup] = useState<EnableResult | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível concluir.");
    } finally {
      setBusy(false);
    }
  };

  const enable = (e: SyntheticEvent) => {
    e.preventDefault();
    void run(async () => {
      const r = await authPost<EnableResult>("/two-factor/enable", { password });
      setSetup(r);
      setPassword("");
    });
  };
  const verify = (e: SyntheticEvent) => {
    e.preventDefault();
    void run(async () => {
      await authPost("/two-factor/verify-totp", { code: code.trim() });
      setSetup(null);
      setCode("");
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success("Verificação em duas etapas ativada.");
    });
  };
  const disable = (e: SyntheticEvent) => {
    e.preventDefault();
    void run(async () => {
      await authPost("/two-factor/disable", { password });
      setPassword("");
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success("Verificação em duas etapas desativada.");
    });
  };

  const workspaceId = lastWorkspaceId();

  return (
    <div className="flex min-h-screen flex-col bg-muted/40">
      <header className="flex items-center justify-between border-b bg-background px-6 py-3">
        <Logo />
        <ThemeToggle />
      </header>
      <main className="mx-auto w-full max-w-xl flex-1 space-y-6 p-6">
        <Button asChild variant="ghost" size="sm">
          {workspaceId ? (
            <Link to="/app/$workspaceId" params={{ workspaceId }}>
              <ArrowLeft /> Voltar
            </Link>
          ) : (
            <Link to="/app">
              <ArrowLeft /> Voltar
            </Link>
          )}
        </Button>
        {me.isPending ? (
          <LoadingScreen />
        ) : me.isError ? (
          <ErrorState error={me.error} onRetry={() => void me.refetch()} />
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CardTitle>Verificação em duas etapas</CardTitle>
                {me.data.twoFactorEnabled ? (
                  <Badge variant="success">
                    <ShieldCheck className="mr-1 size-3" aria-hidden />
                    Ativa
                  </Badge>
                ) : (
                  <Badge variant="secondary">Desativada</Badge>
                )}
              </div>
              <CardDescription>
                Além da senha, o login pede um código de 6 números do aplicativo autenticador (Google Authenticator, 1Password, Authy…).
                {me.data.isSuperAdmin ? " Obrigatória para o painel do dono do SaaS." : ""}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {error ? (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
              {setup ? (
                <form onSubmit={verify} className="space-y-4">
                  <ol className="list-decimal space-y-2 pl-5 text-sm">
                    <li>
                      No aplicativo autenticador, adicione uma conta com a chave:
                      <code className="mt-1 block rounded bg-muted px-2 py-1 font-mono text-sm break-all">{secretOf(setup.totpURI)}</code>
                      <span className="text-xs text-muted-foreground">
                        (ou cole o endereço <span className="break-all">{setup.totpURI}</span>)
                      </span>
                    </li>
                    <li>
                      Guarde os códigos de recuperação (cada um vale uma vez):
                      <code className="mt-1 grid grid-cols-2 gap-1 rounded bg-muted px-2 py-1 font-mono text-xs">
                        {setup.backupCodes.map((c) => (
                          <span key={c}>{c}</span>
                        ))}
                      </code>
                    </li>
                    <li>Digite o código que o aplicativo mostra agora.</li>
                  </ol>
                  <FormField id="codigo-2fa" label="Código de 6 números">
                    <Input id="codigo-2fa" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
                  </FormField>
                  <Button type="submit" loading={busy} disabled={code.length !== 6}>
                    Ativar
                  </Button>
                </form>
              ) : (
                <form onSubmit={me.data.twoFactorEnabled ? disable : enable} className="space-y-4">
                  <FormField id="senha-2fa" label="Sua senha" hint="Contas criadas só com link mágico ou Google precisam definir uma senha antes (Esqueci a senha).">
                    <Input id="senha-2fa" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                  </FormField>
                  <Button type="submit" variant={me.data.twoFactorEnabled ? "outline" : "default"} loading={busy} disabled={password.length < 8}>
                    {me.data.twoFactorEnabled ? "Desativar 2FA" : "Configurar 2FA"}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
